import { z } from 'zod';
// Form data used by consumer facing website and native apps.
import * as actions from '../actions';
import type { ModifierGroup } from '../schemas/menu/modifierGroupSchema';
import { type Modifier, modifierSchema } from '../schemas/menu/modifierSchema';
import {
  initOrderItemModifier,
  type OrderItemModifier,
} from '../schemas/order/orderItemModifierSchema';
import { type OrderItem, orderItemSchema } from '../schemas/order/orderItemSchema';

export const orderItemFormDataSchema = z.object({
  notes: orderItemSchema.shape.notes,
  qty: orderItemSchema.shape.qty,
  selectedManyModifiers: z.array(modifierSchema), // extra
  // It's important that the selectedOneModifiers array index matches the oneModifierGroup index,
  // so that the form renders correctly. optional() allows for mod groups where minQty allowed is 0.
  selectedOneModifiers: z.array(modifierSchema.optional()), // one
  selectedSizeModifier: modifierSchema.optional(), // size
});
export type OrderItemFormData = z.output<typeof orderItemFormDataSchema>;

/***
 * Given an OrderItem, return the selected/default values for the form.
 * Get a list of default modifiers (for each mod group).
 * Use modifierGroup.defaultModifierId (if set), otherwise use the first item.
 */
export const getOrderItemDefaultValues = (
  orderItem: OrderItem,
  modifierGroups: ModifierGroup[],
  modifiers: Modifier[],
): OrderItemFormData => {
  const oneModifierGroups = modifierGroups.filter(
    (mg) => mg.qtyMax === 1 && mg.uuid !== orderItem.menuItemSizeModifierGroupUuid,
  );
  const manyModifierGroups = modifierGroups.filter((mg) => mg.qtyMax > 1);

  // Note! `orderItem.modifiers` are of type `OrderItemModifier`, but we need `Modifier` here.
  // First, get a list of (ONE) modifier id's for the current order item.
  const oneOrderItemModifierIds: string[] = orderItem.modifiers
    .filter((m) => oneModifierGroups.map((mg) => mg.uuid).includes(m.modifierGroupUuid))
    .map((m) => m.modifierUuid);
  // Then, get a list of (ONE) modifiers. Note, it's Modifier, not OrderItemModifier!
  const selectedOneModifiers: Modifier[] = modifiers.filter((m) =>
    oneOrderItemModifierIds.includes(m.uuid),
  );

  // Size modifier
  const sizeOrderItemModifierId: string | undefined = orderItem.modifiers.find(
    (oim) => oim.modifierGroupUuid === orderItem.menuItemSizeModifierGroupUuid,
  )?.modifierUuid;
  const selectedSizeModifier: Modifier | undefined = modifiers.find(
    (m) => m.uuid === sizeOrderItemModifierId,
  );
  const defaultSizeModifier: Modifier | undefined = modifiers.filter(
    (m) => m.modifierGroupUuid === orderItem.menuItemSizeModifierGroupUuid,
  )[0];

  // Next, do the same for MANY mods.
  const manyOrderItemModifierIds: string[] = orderItem.modifiers
    .filter((m) => manyModifierGroups.map((mg) => mg.uuid).includes(m.modifierGroupUuid))
    .map((m) => m.modifierUuid);
  // Then, get a list of (MANY) modifiers.
  const selectedManyModifiers: Modifier[] = modifiers.filter((m) =>
    manyOrderItemModifierIds.includes(m.uuid),
  );

  // Ensure the selectedOneModifiers array is ordered according to the oneModifierGroups array.
  // Loop through each item in oneModifierGroups since this is the array that dictates the sort order.
  const orderedSelectedOneModifiers = oneModifierGroups.map((mg) => {
    const modifier = selectedOneModifiers.find(
      (modifier) => modifier.modifierGroupUuid === mg.uuid,
    );

    const groupModifiers = modifiers.filter((m) => m.modifierGroupUuid === mg.uuid);
    const defaultModifier = groupModifiers.find((m) => mg.defaultModifierUuid === m.uuid);
    const firstModifier = groupModifiers[0];

    if (mg.qtyMin === 0) {
      return modifier || defaultModifier;
    }
    // If the qtyMin is > 0, meaning that a value must be chosen, then include
    // the `firstModifier` which ensure a value is selected.
    else {
      return modifier || defaultModifier || firstModifier;
    }
  });

  return {
    notes: orderItem.notes ?? '',
    qty: orderItem.qty,
    //  Use `menuItem.comesWithModifiersIds`..
    selectedManyModifiers: selectedManyModifiers.length > 0 ? selectedManyModifiers : [],
    // TODO: Add support for component/default values for extra mods (ie defaultModifierIds).
    selectedOneModifiers: orderedSelectedOneModifiers,
    selectedSizeModifier: selectedSizeModifier ?? defaultSizeModifier,
  };
};

export const applyOrderItemFormData = (
  orderItem: OrderItem,
  modifierGroups: ModifierGroup[],
  formData: OrderItemFormData,
): OrderItem => {
  let updatedOrderItem = orderItemSchema.parse(JSON.parse(JSON.stringify(orderItem)));

  // First, get all the selected modifiers.
  const allModifiers = [...formData.selectedOneModifiers, ...formData.selectedManyModifiers];
  if (formData.selectedSizeModifier) {
    allModifiers.push(formData.selectedSizeModifier);
  }

  // Then, convert the select Modifier's to OrderItemModifier's.
  const orderItemModifiers: OrderItemModifier[] = allModifiers
    .filter((m) => !!m)
    .map((m) => {
      const modifierGroup = modifierGroups.find((mg) => mg.uuid === m.modifierGroupUuid);
      if (!modifierGroup) {
        throw new Error(`ModifierGroup not found for modifier: ${m.uuid}`);
      }
      return initOrderItemModifier({ modifier: m, modifierGroup: modifierGroup });
    });

  // Add these order item mods to the order item (so totals etc. are calculated)
  updatedOrderItem.modifiers = []; // Clear any existing mods.
  for (const orderItemModifier of orderItemModifiers) {
    updatedOrderItem = actions.orderItem.addModifier(updatedOrderItem, orderItemModifier);
  }

  // Add notes
  updatedOrderItem = actions.orderItem.setNotes(updatedOrderItem, formData.notes);

  // Set qty
  updatedOrderItem = actions.orderItem.setQty(updatedOrderItem, formData.qty);

  return updatedOrderItem;
};
